/**
 * Deterministic RNG. The single source of randomness in THE LAST ROLL.
 *
 * `Math.random()` is banned repo-wide (see CLAUDE.md §4.1) because the whole
 * game depends on `(seed, event sequence) -> identical final state`: replay,
 * save integrity, NPC memory consistency, and audio/visual variation that
 * matches on replay all fall out of that property.
 *
 * Streams are independent: consuming `dice` must never shift `loot`, otherwise
 * a cosmetic change (an extra shimmer roll) would alter drop tables.
 */

export type RngStream = 'dice' | 'loot' | 'encounter' | 'world' | 'cosmetic' | 'deck';

export const RNG_STREAMS: readonly RngStream[] = [
  'dice',
  'loot',
  'encounter',
  'world',
  'cosmetic',
  'deck',
] as const;

/** 32-bit string hash (FNV-1a). Used to derive per-stream seeds from a run seed. */
export function hashString(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * mulberry32 — small, fast, well-distributed for game use, and trivially
 * serialisable (a single uint32 of state), which is what lets us snapshot a
 * run mid-adventure and resume with bit-identical results.
 */
export class Rng {
  private s: number;

  constructor(seed: number) {
    this.s = seed >>> 0;
  }

  /** Serialisable cursor. Store this in a snapshot, restore with `Rng.restore`. */
  get state(): number {
    return this.s >>> 0;
  }

  static restore(state: number): Rng {
    return new Rng(state);
  }

  /** Uniform float in [0, 1). */
  next(): number {
    this.s = (this.s + 0x6d2b79f5) >>> 0;
    let t = this.s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Integer in [min, max] inclusive. */
  int(min: number, max: number): number {
    if (max < min) throw new Error(`Rng.int: max ${max} < min ${min}`);
    return min + Math.floor(this.next() * (max - min + 1));
  }

  /** A single d6. The most important function in the game. */
  d6(): number {
    return this.int(1, 6);
  }

  float(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  bool(probability = 0.5): boolean {
    return this.next() < probability;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('Rng.pick: empty array');
    return items[this.int(0, items.length - 1)] as T;
  }

  /** Weighted pick. Weights must be non-negative; zero-weight entries never appear. */
  weighted<T>(items: readonly { value: T; weight: number }[]): T {
    const total = items.reduce((sum, it) => sum + Math.max(0, it.weight), 0);
    if (total <= 0) throw new Error('Rng.weighted: total weight must be > 0');
    let roll = this.next() * total;
    for (const it of items) {
      roll -= Math.max(0, it.weight);
      if (roll < 0) return it.value;
    }
    return items[items.length - 1]!.value;
  }

  /** Fisher-Yates. Returns a new array; does not mutate the input. */
  shuffle<T>(items: readonly T[]): T[] {
    const out = items.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = this.int(0, i);
      [out[i], out[j]] = [out[j] as T, out[i] as T];
    }
    return out;
  }
}

/** All five streams for one run, derived from a single human-shareable seed. */
export class RngBundle {
  private readonly streams: Record<RngStream, Rng>;

  constructor(runSeed: string | number) {
    const base = typeof runSeed === 'number' ? runSeed >>> 0 : hashString(runSeed);
    this.streams = {} as Record<RngStream, Rng>;
    for (const name of RNG_STREAMS) {
      // Mix the stream name in so two runs sharing a seed still diverge per stream.
      this.streams[name] = new Rng((base ^ hashString(name)) >>> 0);
    }
  }

  get(stream: RngStream): Rng {
    return this.streams[stream];
  }

  /** Snapshot every cursor so a save resumes mid-run with identical results. */
  serialize(): Record<RngStream, number> {
    const out = {} as Record<RngStream, number>;
    for (const name of RNG_STREAMS) out[name] = this.streams[name].state;
    return out;
  }

  restore(states: Partial<Record<RngStream, number>>): void {
    for (const name of RNG_STREAMS) {
      const s = states[name];
      if (typeof s === 'number') this.streams[name] = Rng.restore(s);
    }
  }
}

/**
 * Derives a stable sub-seed for presentation-only variation (dice tumble path,
 * bounce timing, shimmer offsets). Derived rather than drawn from a stream so
 * that visuals/audio can vary without perturbing gameplay randomness.
 */
export function presentationSeed(runSeed: string | number, rollSeq: number): number {
  const base = typeof runSeed === 'number' ? runSeed >>> 0 : hashString(runSeed);
  return (base ^ hashString(`present:${rollSeq}`)) >>> 0;
}
